
  import { defineComponent, ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { useAbility } from '@casl/vue';
  import { useI18n } from 'vue-i18n/index';
  import { Actions } from '@/store/enums/StoreEnums';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import router from '@/router';
  import PurchaseLogsAdvancedSearch from '@/components/search/PurchaseLogsAdvancedSearch.vue';

  export default defineComponent({
    name: 'purchase-logs-listing',
    components: {
      Datatable,
      PurchaseLogsAdvancedSearch,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const { can } = useAbility();
      const translate = (key) => (te(key) ? t(key) : key);
      const loading = ref(false);
      const tableHeader = ref([
        { name: translate('actions'), key: 'actions', sortable: false },
        { name: translate('ID'), key: 'id', sortable: true },
        {
          name: translate('purchaseDateTime'),
          key: 'purchaseDateTime',
          sortable: true,
        },
        { name: translate('service'), key: 'service', sortable: true },
        { name: translate('packageName'), key: 'packageName', sortable: true },
        {
          name: translate('customerName'),
          key: 'customerName',
          sortable: true,
        },
        { name: translate('amount'), key: 'amount', sortable: true },
        { name: translate('phoneNumber'), key: 'phoneNumber', sortable: true },
        { name: translate('accountType'), key: 'accountType', sortable: true },
        { name: translate('status'), key: 'status', sortable: true },
      ]);

      const fetchChargeLogs = async () => {
        loading.value = true;
        await store.dispatch(Actions.GET_PURCHASE_LOGS);
        loading.value = false;
      };

      const pageChanged = async (val: number) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PURCHASE_LOGS_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_PURCHASE_LOGS);
        loading.value = false;
      };

      const statusClass = (status: string) => {
        switch (status) {
          case 'PAID':
            return 'badge badge-light-success';
          case 'UNPAID':
            return 'badge badge-light-danger';
          case 'CAPTURED':
            return 'badge badge-light-success';
          case 'DECLINED':
            return 'badge badge-light-danger';
          case 'ABANDONED':
            return 'badge badge-light-warning';
          default:
            return 'badge badge-light-secondary';
        }
      };

      const accountTypeClass = (accountType: string) => {
        switch (accountType) {
          case 'Office':
            return 'badge badge-light-primary';
          case 'Individual':
            return 'badge badge-light-info';
          default:
            return 'badge badge-light-secondary';
        }
      };

      const onReset = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PURCHASE_LOGS_FILTER, value);
        await store.dispatch(Actions.UPDATE_PURCHASE_LOGS_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_PURCHASE_LOGS);
        loading.value = false;
      };

      const onTypeChange = async (values) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PURCHASE_LOGS_FILTER, values);
        await store.dispatch(Actions.UPDATE_PURCHASE_LOGS_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_PURCHASE_LOGS);
        loading.value = false;
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          // Handle error if needed
        }
      };

      const overviewPurchase = async (id) => {
        router.push({ name: 'purchase-logs-overview', params: { id } });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      const goTo = (pageName: string) => {
        router.push({ name: pageName });
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('purchaseLogs'), [
          translate('subscriptions'),
        ]);
        await fetchChargeLogs();
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      });

      return {
        tableData: computed(() => store.getters.purchaseLogsList),
        totalItems: computed(() => store.getters.getpurchaseLogsCount),
        tableHeader,
        loading,
        translate,
        can,
        pageChanged,
        statusClass,
        accountTypeClass,
        onTypeChange,
        onReset,
        overviewPurchase,
        goTo,
      };
    },
  });
